import { useMemo, useState, useCallback, useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  useGetConsignmentLinesQuery,
  useGetPalletGroupQuery,
  useUpdatePalletGroup,
  CONSIGNMENT_STATUS,
  ConsignmentStatusCode,
  fromPalletGroup,
  lineTotals,
  maxUnitsOnPallet,
  movePortions,
  parseInventoryUnits,
  removePallet,
  setUnitsOnPallet,
  unassignPortions,
  type PalletConfig,
  type PalletPortion
} from '@/entities/consignments';
import { getApiErrorMessage, usePermissions, useProjectSiteParams } from '@/shared/lib';

import { buildPallets, groupLinesByPallet } from '../lib/build-pallet-view';
import type { LocalPallet, PalletGroupView, PalletLineGroup } from './types';

export interface UsePalletViewParams {
  consignmentId: string;
  consignmentStatus?: ConsignmentStatusCode;
  isReadOnly?: boolean;
  /** Selected parts of lines: on a pallet, or loose (`palletId: null`); the caller owns them */
  selectedPortions: PalletPortion[];
  onSelectionChange: Dispatch<SetStateAction<PalletPortion[]>>;
}

export interface UsePalletViewReturn {
  // ─── Core Data ─────────────────────────────────────────────────────────
  pallets: LocalPallet[];
  unassignedLines: PalletLineGroup['lines'];
  palletGroups: PalletGroupView[];
  isLoading: boolean;
  error: unknown;
  /** Whether the user may create, delete and fill pallets */
  canManage: boolean;
  /** A save is running: every other change waits until it ends */
  isSaving: boolean;

  // ─── Selection State ───────────────────────────────────────────────────
  /** Selected line ids of one table: a pallet's, or the unassigned one (`null`) */
  selectedLineIdsOf: (palletId: string | null) => string[];
  /** Replace the selection of one table */
  selectLines: (palletId: string | null, lineIds: string[]) => void;
  /** Some selected part sits on a pallet */
  hasSelectedInPallets: boolean;

  // ─── UI State ──────────────────────────────────────────────────────────
  /** Expanded pallets; a new pallet shows up expanded, whoever created it */
  expandedRows: Set<string>;
  toggleExpanded: (palletId: string) => void;

  // ─── Actions ───────────────────────────────────────────────────────────
  /** Delete the pallet; its units become loose */
  deletePallet: (palletId: string) => Promise<void>;
  /** Move the selected parts onto the pallet */
  moveSelectedToPallet: (palletId: string) => Promise<void>;
  /** Take the selected parts off their pallets */
  unassignSelected: () => Promise<void>;
  /** Set the units of the line on the pallet; 0 takes it off */
  setUnits: (palletId: string, lineId: string, units: number) => Promise<void>;
}

export function usePalletView({
  consignmentId,
  consignmentStatus = CONSIGNMENT_STATUS.NR,
  isReadOnly = false,
  selectedPortions,
  onSelectionChange
}: UsePalletViewParams): UsePalletViewReturn {
  const { t } = useTranslation();
  const { projectId, siteId } = useProjectSiteParams();
  // ─── Permissions & API Data ───────────────────────────────────────────────
  const { useConsignmentLinePermissions } = usePermissions();
  const canManage = useConsignmentLinePermissions(isReadOnly, consignmentStatus).canEditLine;

  const params = useMemo(() => ({ consignmentId, projectId, siteId }), [consignmentId, projectId, siteId]);
  const skip = !consignmentId || !projectId || !siteId;
  const linesQuery = useGetConsignmentLinesQuery(params, { skip });
  const groupQuery = useGetPalletGroupQuery(params, { skip });

  const onSaveError = useCallback(
    (error: unknown) => toast.error(getApiErrorMessage(error, t('pallet.error.saveFailed'))),
    [t]
  );
  const { savePalletConfig, isSaving } = useUpdatePalletGroup({ onError: onSaveError });
  const save = useCallback((next: PalletConfig) => savePalletConfig(params, next), [savePalletConfig, params]);

  // ─── State ─────────────────────────────────────────────────────────────────
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // ─── Computed Values ───────────────────────────────────────────────────────
  const { data } = linesQuery;
  const lines = useMemo(() => data?.items ?? [], [data]);
  const lineById = useMemo(() => new Map(lines.map((line) => [line.id, line])), [lines]);
  const totals = useMemo(() => lineTotals(lines), [lines]);
  const config = useMemo(() => fromPalletGroup(groupQuery.data), [groupQuery.data]);
  const pallets = useMemo(() => buildPallets(config, data?.pallets, lines), [config, data, lines]);
  const { unassignedLines, palletGroups: lineGroups } = useMemo(
    () => groupLinesByPallet(lines, pallets, config, totals),
    [lines, pallets, config, totals]
  );

  const knownPalletIds = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!groupQuery.isSuccess) return;
    const ids = config.map((unit) => unit.uuid);
    const known = knownPalletIds.current;
    knownPalletIds.current = new Set(ids);
    if (!known) return;

    const added = ids.filter((id) => !known.has(id));
    if (added.length === 0) return;
    setExpandedRows((prev) => new Set([...prev, ...added]));
    onSelectionChange([]);
  }, [groupQuery.isSuccess, config, onSelectionChange]);

  // ─── Selection ─────────────────────────────────────────────────────────────
  const selectedLineIdsOf = useCallback(
    (palletId: string | null) =>
      selectedPortions.filter((portion) => portion.palletId === palletId).map((portion) => portion.lineId),
    [selectedPortions]
  );

  const selectLines = useCallback(
    (palletId: string | null, lineIds: string[]) =>
      onSelectionChange((prev) => [
        ...prev.filter((portion) => portion.palletId !== palletId),
        ...lineIds.map((lineId) => ({ palletId, lineId }))
      ]),
    [onSelectionChange]
  );

  const hasSelectedInPallets = selectedPortions.some((portion) => portion.palletId != null);

  // ─── UI Actions ────────────────────────────────────────────────────────────
  const toggleExpanded = useCallback((palletId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(palletId)) {
        next.delete(palletId);
      } else {
        next.add(palletId);
      }
      return next;
    });
  }, []);

  // ─── Business Actions ──────────────────────────────────────────────────────
  const deletePallet = useCallback(
    async (palletId: string) => {
      if (!(await save(removePallet(config, palletId)))) return;
      onSelectionChange((prev) => prev.filter((portion) => portion.palletId !== palletId));
    },
    [config, save, onSelectionChange]
  );

  const moveSelectedToPallet = useCallback(
    async (palletId: string) => {
      if (!(await save(movePortions(config, selectedPortions, palletId, totals)))) return;
      setExpandedRows((prev) => new Set([...prev, palletId]));
      onSelectionChange([]);
    },
    [config, selectedPortions, totals, save, onSelectionChange]
  );

  const unassignSelected = useCallback(async () => {
    if (!(await save(unassignPortions(config, selectedPortions)))) return;
    onSelectionChange([]);
  }, [config, selectedPortions, save, onSelectionChange]);

  const setUnits = useCallback(
    async (palletId: string, lineId: string, units: number) => {
      if (!(await save(setUnitsOnPallet(config, palletId, lineId, units, totals[lineId] ?? 0)))) return;
      if (units > 0) return;
      onSelectionChange((prev) => prev.filter((portion) => portion.palletId !== palletId || portion.lineId !== lineId));
    },
    [config, totals, save, onSelectionChange]
  );

  const palletGroups = useMemo(
    () =>
      lineGroups.map((group) => ({
        ...group,
        unitFields: canManage
          ? Object.fromEntries(
              group.lines.map((line) => [
                line.id,
                {
                  units: group.pallet.units[line.id],
                  max: maxUnitsOnPallet(config, group.pallet.id, line.id, totals[line.id] ?? 0),
                  total: totals[line.id] ?? 0,
                  unit: parseInventoryUnits(lineById.get(line.id)?.inventoryUnits).unit,
                  disabled: isSaving,
                  onCommit: (units: number) => setUnits(group.pallet.id, line.id, units)
                }
              ])
            )
          : null
      })),
    [lineGroups, canManage, config, totals, lineById, isSaving, setUnits]
  );

  return {
    // Data
    pallets,
    unassignedLines,
    palletGroups,
    isLoading: linesQuery.isLoading || groupQuery.isLoading,
    error: linesQuery.error ?? groupQuery.error,
    canManage,
    isSaving,

    // Selection
    selectedLineIdsOf,
    selectLines,
    hasSelectedInPallets,

    // UI
    expandedRows,
    toggleExpanded,

    // Actions
    deletePallet,
    moveSelectedToPallet,
    unassignSelected,
    setUnits
  };
}
